import React, { useState } from 'react';
import { Row, Alert, Input, Select, Divider, Col } from 'antd';
import styled from 'styled-components';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ChartGroup } from './ChartGroup';
import { useGetAnalyticsChartsQuery, useGetMetadataAnalyticsChartsQuery } from '../../../graphql/analytics.generated';
import { useGetHighlightsQuery } from '../../../graphql/highlights.generated';
import { Highlight } from './Highlight';
import { useListDomainsForWidgetQuery } from '../../../graphql/domain.generated';
import filterSearchQuery from '../../search/utils/filterSearchQuery';
// TODO: Check in the repository if this import is needed
// import { ANTD_GRAY } from '../../entity/shared/constants';
import { useUserContext } from '../../context/useUserContext';
import { ComponentSpinner } from '../../shared/ComponentSpinner';
import { useTranslation } from '../../../i18n/useTranslation';
// const IS_DEV = false

const MetadataAnalyticsPlaceholder = styled.div`
    background: #f4f6f8;
    height: 350px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const DomainSelect = styled(Select)`
    width: 100%;

    &&& {
        .ant-select-selector {
            height: 48px;
            background-color: #ffffff;

            .ant-select-selection-search-input {
                height: 100%;
            }

            .ant-select-selection-placeholder {
                display: flex;
                align-items: center;
                color: #8f9094;
            }

            .ant-select-selection-item {
                display: flex;
                align-items: center;
                color: #4c4e54;
            }
        }
    }
`;

const StyledSearchBar = styled(Input)`
    &&& {
        height: 48px;
        border: none;
        border-radius: 0;

        .ant-input {
            height: auto;
            background-color: transparent;
        }

        .anticon-search svg {
            width: 18px;
            height: 18px;
        }
    }

    @media (max-width: 768px) {
        margin-left: 0 !important;
    }
`;

export const AnalyticsPage = () => {
    const me = useUserContext();
    const canManageDomains = me?.platformPrivileges?.createDomains;
    const { data: chartData, loading: chartLoading, error: chartError } = useGetAnalyticsChartsQuery();
    const { data: highlightData, loading: highlightLoading, error: highlightError } = useGetHighlightsQuery();
    const {
        loading: domainLoading,
        error: domainError,
        data: domainData,
    } = useListDomainsForWidgetQuery({
        skip: !canManageDomains,
        variables: {
            input: {
                start: 0,
                count: 1000,
            },
        },
        fetchPolicy: 'no-cache',
    });
    const [domain, setDomain] = useState('ALL');
    const [stagedQuery, setStagedQuery] = useState('');
    const [query, setQuery] = useState('');

    const { t } = useTranslation();
    const onDomainChange = (inputDomain) => setDomain(inputDomain);
    const onStagedQueryChange = (inputQuery) => setStagedQuery(inputQuery);
    const {
        loading: metadataAnalyticsLoading,
        error: metadataAnalyticsError,
        data: metadataAnalyticsData,
    } = useGetMetadataAnalyticsChartsQuery({
        variables: {
            input: {
                entityType: null,
                domain,
                query,
            },
        },
        skip: domain === '' && query === '',
    });

    const isLoading = highlightLoading || chartLoading || domainLoading || metadataAnalyticsLoading;
    return (
        <>
            {isLoading && <ComponentSpinner />}
            <Row className="f-recommendation-cards-wrapper">
                {highlightError && (
                    <Alert type="error" message={highlightError?.message || t('Highlights failed to load')} />
                )}
                {highlightData?.getHighlights?.map((highlight) => (
                    <Highlight highlight={highlight} shortenValue />
                ))}
            </Row>
            <>
                {chartError && (
                    <Alert type="error" message={metadataAnalyticsError?.message || t('Charts failed to load')} />
                )}
                {chartData?.getAnalyticsCharts
                    ?.filter((chartGroup) => chartGroup.groupId === 'GlobalMetadataAnalytics')
                    .map((chartGroup) => (
                        <ChartGroup chartGroup={chartGroup} />
                    ))}
            </>
            <>
                {domainError && (
                    <Alert type="error" message={metadataAnalyticsError?.message || t('Domains failed to load')} />
                )}
                {!chartLoading && (
                    <>
                        <Row align="middle" className="f-layout-vertical-border">
                            <Col xs={24} sm={12} md={8}>
                                <div className="pa-2">
                                    <DomainSelect
                                        showSearch
                                        placeholder={t('All')}
                                        onChange={onDomainChange}
                                        className="f-width-100-xs"
                                        filterOption={(input, option) =>
                                            option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        aria-label={t('Select Domain')}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                    >
                                        <Select.Option value="ALL" tabIndex={0}>
                                            {t('All')}
                                        </Select.Option>
                                        {domainData?.listDomains?.domains.map((domainChoice) => (
                                            <Select.Option value={domainChoice.urn} tabIndex={0}>
                                                {domainChoice?.properties?.name}
                                            </Select.Option>
                                        ))}
                                    </DomainSelect>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={16}>
                                <Divider className="f-hidden-xs ma-0" />
                                <div className="pa-2">
                                    <StyledSearchBar
                                        value={stagedQuery}
                                        placeholder={t('Search')}
                                        className="f-width-100-xs"
                                        data-testid="analytics-search-input"
                                        onPressEnter={(e) => {
                                            e.stopPropagation();
                                            setQuery(filterSearchQuery(stagedQuery || ''));
                                        }}
                                        onChange={(e) => onStagedQueryChange(e.target.value)}
                                        prefix={
                                            <SearchOutlined
                                                onClick={() => setQuery(filterSearchQuery(stagedQuery || ''))}
                                            />
                                        }
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Divider className="ma-0" />
                    </>
                )}
            </>
            <>
                {metadataAnalyticsError && (
                    <Alert type="error" message={metadataAnalyticsError?.message || t('Charts failed to load')} />
                )}
                {domain === '' && query === ''
                    ? !chartLoading && (
                          <MetadataAnalyticsPlaceholder>
                              <InfoCircleOutlined className="f-icon-size-md f-color-dark-black-s70" />
                              <div className="f-text-big-content f-color-dark-black-s70 mt-2">
                                  {t('Please specify domain or query to get granular results')}
                              </div>
                          </MetadataAnalyticsPlaceholder>
                      )
                    : metadataAnalyticsData?.getMetadataAnalyticsCharts?.map((chartGroup) => (
                          <ChartGroup chartGroup={chartGroup} />
                      ))}
            </>
            <>
                {chartError && <Alert type="error" message={chartError?.message || t('Charts failed to load')} />}
                {!chartLoading &&
                    chartData?.getAnalyticsCharts
                        ?.filter((chartGroup) => chartGroup.groupId === 'DataHubUsageAnalytics')
                        .map((chartGroup) => <ChartGroup chartGroup={chartGroup} />)}
            </>
        </>
    );
};
